import React from 'react';
import { useTranslation } from 'react-i18next';
import type { SingleValue } from 'react-select';

import type { IndustryDto } from '@/services/ws-selfbooking/src/main/resources/rest-api';
import { useCallbackRef } from '@/js/libs/hooks/useCallbackRef';
import { Filter } from '@/js/libs/ui/src/V2';

import type { Option } from 'src/types';
import { formatIndustryOptionLabel } from '../helpers';
import { useIndustriesSelfBookingOptions } from '../hooks/useIndustriesSelfBookingOptions';

interface IndustryFilterProps {
  value: Option<IndustryDto> | null;
  onChange: (value: SingleValue<Option<IndustryDto>>) => void;
  label?: string;
}

export const IndustrySelfBookingFilter: React.FC<IndustryFilterProps> = React.memo(function IndustrySelfBookingFilter({
  value,
  onChange,
  label,
}) {
  const [t] = useTranslation(['backend', 'common']);
  const [, { language }] = useTranslation(['backend']);
  const { options, pending: loading } = useIndustriesSelfBookingOptions();

  const handleChange = useCallbackRef((newValue: SingleValue<Option<IndustryDto>>) => {
    onChange(newValue);
  });

  const translatedFilter = React.useMemo(
    () =>
      value
        ? {
            ...value,
            label: formatIndustryOptionLabel(value.value, language),
          }
        : null,
    [value, language],
  );

  return (
    <Filter<IndustryDto>
      label={label || t('common:INDUSTRY')}
      loading={loading}
      selected={translatedFilter}
      onChange={handleChange}
      options={options}
      dataTestId='industry-dropdown'
    />
  );
});
